import { useValidateBreakpoint } from '@providers/AppProvider/Breakpoint/hooks';
import { useProductListThemeSettings } from '@providers/ThemeProvider/Provider/ProductListThemeSettings';
import React, { useContext, useEffect, useState, useSyncExternalStore } from 'react';

import type {
  AdditionalElementStateProviderProps,
  AdditionalElementStateValue,
  ViewAsGridMultiColListOption,
} from '../types';

const AdditionalElementStateContext = React.createContext<AdditionalElementStateValue | null>(null);

export const AdditionalElementStateProvider = ({
  additionalElementStore,
  children,
}: AdditionalElementStateProviderProps) => {
  const {
    loadingAdditional,
    page,
    limit,
    totalPages,
    totalProducts,
    currentProductCount,
    fromProductIndex,
    toProductIndex,
    sortBy,
    searchPanelTotalCollections,
    searchPanelTotalPages,
    onChangeFilterLimit,
    onChangeFilterSortBy,
  } = useSyncExternalStore(additionalElementStore.subscribe, additionalElementStore.getSnapshot);

  const mobileBreakpoint = useValidateBreakpoint('mobile');

  const { productsPerRowOnDesktop, productsPerRowOnMobile } = useProductListThemeSettings();

  /**
   * Fixed value based on theme settings. For example: Mobile: 2, Desktop: 4 (products per row)
   * It serves as initial value for the viewAsGridMultiColListOption
   * and pass down to the ViewAs component as 'defaultCol' to set correct col when click 'Grid view' icon from 'List view'
   */
  const viewAsDefaultValue = `grid--${
    mobileBreakpoint ? productsPerRowOnMobile : productsPerRowOnDesktop
  }` as ViewAsGridMultiColListOption;

  const [viewAsGridMultiColListOption, setViewAsGridMultiColListOption] =
    useState<ViewAsGridMultiColListOption>(viewAsDefaultValue);

  useEffect(() => {
    setViewAsGridMultiColListOption(viewAsDefaultValue);
  }, [mobileBreakpoint]);

  const providerValue = {
    loadingAdditional,
    page,
    limit,
    totalPages,
    totalProducts,
    currentProductCount,
    fromProductIndex,
    toProductIndex,
    sortBy,
    viewAsGridMultiColListOption,
    viewAsDefaultValue,
    setViewAsGridMultiColListOption,
    onChangeFilterLimit,
    onChangeFilterSortBy,
    searchPanelTotalCollections,
    searchPanelTotalPages,
  };

  return (
    <AdditionalElementStateContext.Provider value={providerValue}>
      {children}
    </AdditionalElementStateContext.Provider>
  );
};

export const useAdditionalElementState = (): AdditionalElementStateValue => {
  const context = useContext(AdditionalElementStateContext);
  if (!context) {
    throw Error('Use useAdditionalElementState in AdditionalElementStateProvider');
  }

  return context;
};
